/*
 * Active discount code -> prices in the booking modules (window.myselfie.discount).
 *
 * /discount/CODE (e.g. myselfie.dk/messe) leaves the code in the discount_code cookie; Shopify applies it in the
 * cart and the checkout. This file finds out what the code gives, so the booking module can show the old price
 * struck through and the price after the discount:
 *
 *   - one throwaway cart through the Storefront API (tokenless, /api/<version>/graphql.json) with the code and one
 *     of each variant the module shows; the customer's own cart is never touched;
 *   - a percentage on all items  -> all(): every price;
 *   - a percentage or a fixed amount per item on some products -> per variant;
 *   - anything else (an amount off the order, free shipping, buy X get Y) is not shown here; the cart shows it.
 *
 * Fails silent: no cookie, an excluded code (the payment codes FAKTURA / EAN / lease, which replace the cookie code
 * at checkout), a code Shopify does not accept or any error -> no discount is shown. The result is kept for the
 * session per code; a call without an answer (network, a page left mid-call) is asked again on the next page.
 * Event: document 'myselfie:discount' when something new is known.
 */
(function () {
  'use strict';

  var M = (window.myselfie = window.myselfie || {});
  if (M.discount) return;

  var API = '/api/2025-07/graphql.json';
  var STORE_KEY = 'myselfie_discount_v2';
  var MAX_LINES = 100;
  var QUERY =
    'mutation($lines:[CartLineInput!],$codes:[String!]){cartCreate(input:{lines:$lines,discountCodes:$codes}){cart{' +
    'discountCodes{code applicable}' +
    ' discountAllocations{discountApplication{targetSelection targetType value{__typename ... on PricingPercentageValue{percentage}}} ... on CartCodeDiscountAllocation{code}}' +
    ' lines(first:' + MAX_LINES + '){nodes{quantity merchandise{... on ProductVariant{id}}' +
    ' discountAllocations{discountedAmount{amount} discountApplication{targetType allocationMethod value{__typename ... on PricingPercentageValue{percentage}}} ... on CartCodeDiscountAllocation{code}}}}' +
    '} userErrors{message}}}';

  /** The code(s) in the cookie /discount/CODE writes (comma separated when there are several). */
  function cookieCodes() {
    var m = document.cookie.match(/(?:^|;\s*)discount_code=([^;]*)/);
    if (!m) return [];
    var raw;
    try { raw = decodeURIComponent(m[1].replace(/\+/g, ' ')); } catch (e) { raw = m[1]; }
    return raw.split(',').map(function (c) { return c.trim(); }).filter(Boolean);
  }

  function upper(c) { return String(c || '').toUpperCase(); }

  var state = { code: '', ok: false, answered: false, all: 0, by: {}, probed: {} };
  var exclude = {};
  var pending = null;
  var queued = {};

  function read() {
    try {
      var s = JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null');
      if (s && s.code) state = s;
    } catch (e) { /* private mode */ }
  }
  function save() {
    try { sessionStorage.setItem(STORE_KEY, JSON.stringify(state)); } catch (e) { /* private mode */ }
  }
  read();

  /** The code that counts now: the first cookie code that is not a payment code. */
  function activeCode() {
    var codes = cookieCodes().filter(function (c) { return !exclude[upper(c)]; });
    return codes[0] || '';
  }

  /** Start over when the cookie holds another code than the one we know. */
  function sync() {
    var code = activeCode();
    if (upper(code) !== upper(state.code)) state = { code: code, ok: false, answered: false, all: 0, by: {}, probed: {} };
    return code;
  }

  function gid(id) { return 'gid://shopify/ProductVariant/' + id; }
  function idOf(g) { return Number(String(g || '').split('/').pop()); }

  function probe(code, ids) {
    return fetch(API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'omit', // a throwaway cart, never the session's
      body: JSON.stringify({ query: QUERY, variables: { codes: [code], lines: ids.map(function (id) { return { merchandiseId: gid(id), quantity: 1 }; }) } }),
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (json) { return json && json.data && json.data.cartCreate && json.data.cartCreate.cart; });
  }

  /** A cart Shopify answered with: a definitive answer for these ids (kept for the session). */
  function apply(cart, code, ids) {
    ids.forEach(function (id) { state.probed[id] = 1; });
    state.answered = true;
    var mine = function (a) { return a && upper(a.code) === upper(code); };
    state.ok = (cart.discountCodes || []).some(function (c) { return upper(c.code) === upper(code) && c.applicable; });
    if (!state.ok) return;
    (cart.discountAllocations || []).forEach(function (a) {
      var app = a.discountApplication || {};
      if (mine(a) && app.targetSelection === 'ALL' && app.targetType === 'LINE_ITEM' && app.value && app.value.percentage) {
        state.all = Math.min(1, app.value.percentage / 100);
      }
    });
    ((cart.lines && cart.lines.nodes) || []).forEach(function (line) {
      var id = idOf(line.merchandise && line.merchandise.id);
      (line.discountAllocations || []).forEach(function (a) {
        var app = a.discountApplication || {};
        if (!mine(a) || app.targetType !== 'LINE_ITEM' || !app.value) return;
        if (app.value.percentage) state.by[id] = { pct: Math.min(1, app.value.percentage / 100) };
        else if (app.allocationMethod === 'EACH' && line.quantity === 1) {
          state.by[id] = { amt: Math.round(Number(a.discountedAmount && a.discountedAmount.amount) * 100) || 0 };
        }
      });
    });
  }

  function run() {
    var code = sync();
    var ids = Object.keys(queued).map(Number).filter(function (id) { return id && !state.probed[id]; });
    queued = {};
    // nothing to ask: no code, a percentage on everything (known after one answer), a code Shopify turned down
    if (!code || !ids.length || (state.ok && state.all) || (state.answered && !state.ok)) return Promise.resolve(false);
    ids = ids.slice(0, MAX_LINES);
    return probe(code, ids)
      .then(function (cart) {
        if (cart) return apply(cart, code, ids);
        // a variant that cannot be sold fails the whole cart: ask with the first one alone (the code itself)
        if (ids.length > 1) {
          return probe(code, ids.slice(0, 1)).then(function (c) {
            if (!c) throw new Error('no answer');
            apply(c, code, ids.slice(0, 1));
            ids.forEach(function (id) { state.probed[id] = 1; });
          });
        }
        throw new Error('no answer');
      })
      .then(function () {
        save();
        return true;
      }, function () {
        // network error, a call cut off by leaving the page, no answer: nothing is kept, the next page asks again
        return false;
      });
  }

  function announce() {
    try {
      document.dispatchEvent(new CustomEvent('myselfie:discount', { detail: { code: state.code, all: state.all } }));
    } catch (e) { /* old browsers: the next render shows it */ }
  }

  M.discount = {
    /**
     * Ask what the active code gives for these variant ids (only ids not asked before). codes: payment codes to
     * ignore (they replace the cookie code at checkout). Resolves true when something new is known.
     */
    load: function (ids, codes) {
      (codes || []).forEach(function (c) { if (c) exclude[upper(c)] = 1; });
      (ids || []).forEach(function (id) { if (id) queued[id] = 1; });
      if (!window.fetch || !window.Promise) return { then: function () {} };
      var chain = (pending || Promise.resolve()).then(run);
      pending = chain.then(function (news) {
        if (news) announce();
        return news;
      });
      return pending;
    },
    /** The code shown with the prices ('' = none). */
    code: function () {
      sync();
      return state.ok && (state.all || Object.keys(state.by).length) ? state.code : '';
    },
    /** Discount in øre for qty (default 1) of the variant at that unit price (0 = none). */
    off: function (variant, price, qty) {
      if (!price || price < 0 || !this.code()) return 0;
      var n = qty || 1;
      var d = variant && variant.id && state.by[variant.id];
      if (d && d.pct) return Math.round(price * n * d.pct);
      if (d && d.amt) return Math.min(price, d.amt) * n;
      return state.all ? Math.round(price * n * state.all) : 0;
    },
  };
})();
